import { useMemo } from 'react';

import { getYear } from 'date-fns';

import { useLocale } from '@adsk/alloy-react-helpers';

import {
  getSelectableMonths,
  getWeekDaysShort,
  getMonths,
  convertYearRangeToArray,
} from '../helpers';
import { DatePickerDate } from '../types';

function useCalendars({
  activeMonths,
  selectedDates,
  minDate,
  maxDate,
  firstDayOfWeek,
  showOutsideDays,
  isDateSelectable,
  yearsToDisplay,
}: {
  activeMonths: { year: number; month: number }[];
  selectedDates: (DatePickerDate | null)[] | null;
  minDate?: Date | null;
  maxDate?: Date | null;
  firstDayOfWeek: number;
  showOutsideDays?: boolean;
  isDateSelectable?: ({ date }: { date: Date }) => boolean;
  yearsToDisplay: number;
}) {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const weekdays = useMemo(
    () => getWeekDaysShort(T, firstDayOfWeek),
    [T, firstDayOfWeek]
  );

  const calendars = useMemo(
    () =>
      activeMonths.map((month, index) => {
        const calendar = getMonths({
          month: month.month,
          year: month.year,
          selectedDates,
          minDate,
          maxDate,
          firstDayOfWeek,
          showOutsideDays,
          isDateSelectable,
        });

        const months = getSelectableMonths({
          T,
          minDate,
          maxDate,
          currentYear: month.year,
        });

        const years = convertYearRangeToArray({
          min: minDate ? getYear(minDate) : month.year - yearsToDisplay,
          max: maxDate ? getYear(maxDate) : month.year + yearsToDisplay,
          selectedDates,
        });

        return {
          ...calendar,
          index,
          months,
          weekdays,
          years,
          selectedMonth: months.find(({ key }) => key === calendar.month),
          selectedYear: years.find(({ key }) => key === calendar.year),
        };
      }),
    [
      T,
      firstDayOfWeek,
      isDateSelectable,
      showOutsideDays,
      weekdays,
      yearsToDisplay,
      activeMonths,
      minDate,
      maxDate,
      selectedDates,
    ]
  );

  return calendars;
}

export default useCalendars;
